import { DateTime } from 'luxon';
import * as z from 'zod';

import { AllBrandedIds } from '../BrandedIds';

export const transformDateOrStringToDate = (value: string | Date, implyTwoThousands = true) =>
  typeof value === 'string' ? convertStringToDate(value, implyTwoThousands) : value;

/**
 * Imply 21st century for 2-digit years if implyTwoThousands is true
 */
export const convertStringToDate = (value: string, implyTwoThousands = true): Date => {
  const hasFourDigitYear = /\d{1,2}\/\d{1,2}\/\d{4}/.test(value);
  if (hasFourDigitYear) {
    // Preserve the year if it's entered fully with 4-digits
    return new Date(value);
  }

  const date = new Date(value);

  // JS date will parse 1/1/50 as 1/1/1950 so adjusting accordingly
  if (date < new Date('1/1/1990') && implyTwoThousands) {
    return DateTime.fromJSDate(date).plus({ years: 100 }).toJSDate();
  }

  return date;
};

/**
 * 2-digit years are interpreted as 20xx
 * Should be used for things like maturity date where 1/1/50 should be 1/1/2050
 */
export const zodDateOrString = z
  .date()
  .or(z.string())
  .transform((value) => transformDateOrStringToDate(value, true))
  .meta({
    id: 'dateString',
    type: 'string',
    effectType: 'input',
    description:
      'A date string is any string parsable with new Date(<string>) in Javascript. Examples include formatted dates such as “10/1/2022” or an ISO 8601 timestamp.',
  });

export const zodDateOrStringImplyNineteenHundreds = z
  .date()
  .or(z.string())
  .transform((value) => transformDateOrStringToDate(value, false))
  .meta({
    type: 'string',
    effectType: 'input',
    description:
      'A date string is any string parsable with new Date(<string>) in Javascript. Examples include formatted dates such as “10/1/2022” or an ISO 8601 timestamp.',
  });

export function zodBrandedUuid<T extends AllBrandedIds>() {
  return z
    .string()
    .uuid()
    .transform((id) => id as T);
}

export function zodBrandedString<T extends AllBrandedIds>() {
  return z
    .string()
    .min(1)
    .transform((id) => id as T);
}

export const zodOptionalString = z
  .string()
  .nullable()
  .optional()
  .transform((item) => (item == null ? undefined : item));

export const trimAndStripString = (val: any) => {
  // Trim strings
  if (typeof val === 'string') {
    val = val.trim();
  }
  // Turn "empty" values into undefined
  if (val === null || val === '') {
    return undefined;
  }
  return val;
};

export const DueMonthDay = z.string().refine(
  (val) => {
    const result = DateTime.fromFormat(val, 'MM/d');
    const result2 = DateTime.fromFormat(val, 'M/d');
    return result.isValid || result2.isValid;
  },
  {
    error: 'Not valid month day',
  },
);

export type DueMonthDay = z.infer<typeof DueMonthDay>;

export const transformNullOrUndefinedToUndefined = (value: any | null | undefined) =>
  value === null ? undefined : value;

/**
 * This is a utility function that will attempt to the validate the input
 * type BEFORE calling `.parse` on it. It should help to catch simple syntax
 * errors so we can avoid runtime errors when possible. If you didn't want
 * to use this function to handle this kind of stuff you could also do:
 *
 * const CustomEvent = z.object({ type: z.literal('custom'), name: z.string() })
 * type TCustomEvent = z.infer<typeof CustomEvent>;
 *
 * DUMB PARSING STRATEGY:
 * CustomEvent.parse({ type: 'wow', name: 123 }); // This won't throw TS errors, BAD!
 *
 * SMARTER PARSING STRATEGY:
 * const myEvent: TCustomEvent = { type: 'wow', name: 123 }; // This will throw a TS error, GOOD!
 * CustomEvent.parse(myEvent);
 *
 * WITH `typedParse`
 * typedParse(CustomEvent, { type: 'wow', name: 123 }); // This will throw a TS error, GOOD!
 *
 */
export const typedParse = <TInput, TOutput>(schema: z.ZodType<TOutput, TInput>, input: TInput) => schema.parse(input);

/**
 * Like typedParse, but uses `.safeParse` under the hood
 */
export const typedSafeParse = <TInput, TOutput>(schema: z.ZodType<TOutput, TInput>, input: TInput) =>
  schema.safeParse(input);
